/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import NextImage from '@/components/NextImage';
import { Link, Text, View } from '@az/starc-ui';
import { useLabels } from '@/hooks/useLabels';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import styles from './VehicleInstallGuideVideoBanner.module.scss';
import { useProductDetails } from '@/features/product/api/getProductDetails';

type VehicleInstallGuideVideoBannerProps = {
  onClick: () => void;
};

const imageSrc = 'https://www.autozone.com/cdn/images/B2C/US/media/icons/D_Banner_Image.jpg';
const bannerIcon = 'https://www.autozone.com/cdn/images/B2C/US/media/icons/D_Banner_Icon.svg';

const labelMap = {
  titlePart1: 'label_VehicleInstallGuideVideoBanner_Title1',
  titlePart2: 'label_VehicleInstallGuideVideoBanner_Title2',
  subtitle: 'label_VehicleInstallGuideVideoBanner_Subtitle',
};

export default function VehicleInstallGuideVideoBanner({
  onClick,
}: VehicleInstallGuideVideoBannerProps) {
  const labels = useLabels(labelMap);
  const preferredVehicle = usePreferredVehicle();

  const vehicle = [preferredVehicle?.year, preferredVehicle?.make, preferredVehicle?.model]
    .filter(Boolean)
    .join(' ');

  const { data: productDetails } = useProductDetails();

  const partTypeName = productDetails?.product?.partTypeName;

  const headerText = [labels.titlePart1, partTypeName, labels.titlePart2, vehicle]
    .filter((part) => typeof part === 'string' && part.trim())
    .join(' ');

  return (
    <Link variant="plain" onClick={onClick}>
      <View className={styles.container} direction="row" align="center">
        <View.Item className={styles.leftImageContainer}>
          <NextImage src={bannerIcon} height={57} width={83} className={styles.image} />
        </View.Item>
        <View.Item className={styles.copyContainer}>
          <View direction="column" className={styles.textContent}>
            <Text className={styles.installCopy}>{headerText}</Text>
            <Text className={styles.seeAllCopy}>{labels.subtitle}</Text>
          </View>
        </View.Item>
        <View.Item className={styles.rightImageContainer}>
          <NextImage src={imageSrc} height={70} width={70} className={styles.imageThumbnail} />
        </View.Item>
      </View>
    </Link>
  );
}
